"use client";

import { useTranslation } from "react-i18next";
import { useProductUsage } from "./hooks/useProductUsage";
import { ChartProductUsageSkeleton } from "./components/chart-product-usage-skeleton";
import { formatCurrency, formatToLocaleString } from "@/core/utils/formatters";
import { productTypeCharacteristics } from "@/core/utils/product-type-characteristics";
import {
  DashboardMetricTile,
  wobaText,
} from "@/dashboard/components/dashboard-ds";
import { compareProductOrder } from "@/dashboard/utils/product-order";

import { ChartProductUsageProps } from "./types";
import { useMemo } from "react";
import { DashboardEmptyState } from "../dashboard-empty-state";

/**
 * Visão financeira de Meus produtos (Figma 6038:3414): tile do total do
 * período + um tile por produto, em crédito ou em real conforme o toggle.
 */
export function DashboardChartProductUsage({
  startDate,
  endDate,
  city,
  group_id,
  type,
  status,
  users,
  spaces,
  viewMode,
}: ChartProductUsageProps) {
  const [translate] = useTranslation(["dashboard"]);

  const { data, isLoading, error } = useProductUsage({
    startDate,
    endDate,
    city,
    group_id,
    type,
    status,
    users,
    spaces,
  });

  const verifyEmptyData = useMemo(() => {
    if (isLoading || error) return false;

    if (!data || !data.products || data.products.length === 0) {
      return true;
    }

    // Verifica se todos os valores são zero
    const hasNoMonetaryValue = parseFloat(data.total.monetary_value) === 0;
    const hasNoCreditsValue = data.total.credits_value === 0;

    return hasNoMonetaryValue && hasNoCreditsValue;
  }, [data, isLoading, error]);

  const getName = (productType: number) => {
    const name =
      productTypeCharacteristics[
        productType as unknown as keyof typeof productTypeCharacteristics
      ]?.name;
    return name ? translate(name) : `${productType}`;
  };

  const formatValue = (monetary: string, credits: number) =>
    viewMode === "currency"
      ? formatCurrency(monetary ?? "0")
      : formatToLocaleString(credits ?? 0);

  const creditsCaption = (credits: number) =>
    viewMode === "currency"
      ? undefined
      : (translate(
          credits === 1
            ? "dashboard-my-products-credit-singular"
            : "dashboard-my-products-credit-plural",
        ) as string);

  const products = useMemo(
    () =>
      [...(data?.products ?? [])].sort((a, b) =>
        compareProductOrder(a.type, b.type),
      ),
    [data],
  );

  return (
    <div
      className="flex flex-col gap-4 min-w-0"
      data-testid="chart-product-usage"
    >
      <h3 style={wobaText.captionSmall}>
        {translate("dashboard-chart-product-usage-title")}
      </h3>

      {isLoading && <ChartProductUsageSkeleton />}

      {(verifyEmptyData || error) && <DashboardEmptyState />}

      {!isLoading && !error && !verifyEmptyData && data && (
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <DashboardMetricTile
            data-testid="chart-product-usage-total"
            overline={translate(
              "dashboard-chart-product-usage-total-value-title",
            )}
            value={formatValue(
              data.total.monetary_value,
              data.total.credits_value,
            )}
            caption={creditsCaption(data.total.credits_value)}
          />

          {products.map((product) => (
            <DashboardMetricTile
              key={product.type}
              data-testid={`chart-product-usage-tile-${product.type}`}
              overline={getName(product.type)}
              value={formatValue(product.monetary_value, product.credits_value)}
              caption={creditsCaption(product.credits_value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
